import type { QueryClient } from '@tanstack/react-query';
import {
  getActiveViewerSensitiveQueries,
  invalidateViewerSensitiveQueries,
  purgeViewerSensitiveQueries,
  type ViewerSensitiveRecoveryTarget,
} from '../../cache/persist/viewer-sensitive-query-registry.js';
import { sdkEvents } from '../../core/events.js';
import { refetchRealtimeQuery } from '../../core/internal/refetchRealtimeQuery.js';
import type { IdentityFlipStrategy } from './auth-identity.js';

export async function prepareIdentityFlipQueries(
  queryClient: QueryClient,
  strategy: IdentityFlipStrategy,
): Promise<readonly ViewerSensitiveRecoveryTarget[]> {
  const targets = getActiveViewerSensitiveQueries(queryClient);
  if (strategy === 'hard-purge') {
    await purgeViewerSensitiveQueries(queryClient);
  } else {
    await invalidateViewerSensitiveQueries(queryClient, 'none');
  }
  return targets;
}

export async function recoverIdentityFlipQueries(
  queryClient: QueryClient,
  targets: readonly ViewerSensitiveRecoveryTarget[],
  strategy: IdentityFlipStrategy,
): Promise<void> {
  await Promise.all(
    targets.map(async (target) => {
      let outcome: 'success' | 'error' | 'no_observer';
      try {
        outcome = await refetchRealtimeQuery(queryClient, target.queryKey);
      } catch {
        outcome = 'error';
      }
      // Subscriber-only entity observers have no data-owning query function;
      // their parent normalized list refetch repopulates them. Only a real
      // transport/query error is an independent recovery failure.
      if (outcome !== 'error') return;
      sdkEvents.emit('error', {
        category: 'realtime-degraded',
        code: 'IDENTITY_FLIP_QUERY_RECOVERY_FAILED',
        devMessage: `Identity-flip query recovery failed (${strategy}, ${outcome})`,
        phase: 'refetch',
        queryHash: target.queryHash,
        specName: target.specName,
        strategy,
      });
    }),
  );
}
